import { Form, Formik } from "formik";
import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import FormFullWidthContainer from "../../ui/layout/page/forms-container/form-full-width-container";
import SectionItemContainer from "../../ui/layout/page/forms-container/section-item-container";
import TypographyContainer from "../../ui/layout/page/typography-container/typography-base-container";
import TypographyItemContainer from "../../ui/layout/page/typography-container/typography-item-container";
import FlowHeader from "../../ui/widgets/flow-header";
import CustomLink from "../../ui/widgets/link";
import Typography from "../../ui/widgets/typography";
import CompanyDisplay from "./company-display";
import CompanySelect from "./company-select";
import EnterCompanyForm from "./enter-company-form";
import { HomeModel } from "./types";
import useHomeValidationSchema from "./validator";
import { LanguageContext } from "../../context/languageContext";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import ProductOffer from "./product-offer";
import ProductFamilySelectionModal from "./product-family-selection-modal";
import { Product } from "../../context/product-context/product";

export type HomeProps = {
  initial: HomeModel;
  next: (model: HomeModel) => void;
  // optional as when logged in, product family is known.
  handleOnProductFamilySelection?: (product: Product) => void;
};

export default function Home({
  initial,
  next,
  handleOnProductFamilySelection,
}: HomeProps): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const { language } = useContext(LanguageContext);

  return (
    <>
      {handleOnProductFamilySelection && (
        <ProductFamilySelectionModal
          handleOnProductFamilySelection={handleOnProductFamilySelection}
        />
      )}
      <FlowHeader title={getMessage("FlowHeader.basicAccountApplication")} />
      <ApplicationPageContent>
        <Formik
          key={language}
          initialValues={initial}
          enableReinitialize
          validationSchema={useHomeValidationSchema()}
          onSubmit={(values: HomeModel) => {
            next(values);
          }}
        >
          {({
            values,
            setFieldValue,
            handleSubmit,
            submitCount,
            isSubmitting,
            resetForm,
            errors,
          }) => {
            return (
              <Form key={language}>
                <FormFullWidthContainer>
                  <TypographyContainer>
                    <TypographyItemContainer>
                      <Typography variant="h3">
                        {getMessage("ProductOrder")}
                      </Typography>
                    </TypographyItemContainer>
                  </TypographyContainer>
                  {values.isCompanyManuallyEntered ? (
                    <EnterCompanyForm
                      onBack={() => {
                        resetForm({ values });
                        setFieldValue("isCompanyManuallyEntered", false);
                      }}
                      dataPath="manuallyEnteredCompany."
                      company={values.manuallyEnteredCompany}
                    />
                  ) : (
                    <>
                      <CompanySelect
                        selectedCompany={values.selectedCompany}
                        setSelectedCompany={(data) => {
                          setFieldValue("selectedCompany", data);
                        }}
                      />
                      <CompanyDisplay company={values.selectedCompany} />
                      <SectionItemContainer>
                        <CustomLink
                          onClick={() => {
                            resetForm({ values });
                            setFieldValue("isCompanyManuallyEntered", true);
                          }}
                        >
                          {getMessage("Company.manuallyEnter")}
                        </CustomLink>
                      </SectionItemContainer>
                    </>
                  )}
                </FormFullWidthContainer>
                <div className="scd-section-helper-md" />
                <ProductOffer />
                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  <NavigationBar
                    isSubmitting={isSubmitting}
                    onNext={handleSubmit}
                    nextButtonText={getMessage("Onboarding.continue")}
                  />
                </ContentFooter>
              </Form>
            );
          }}
        </Formik>
      </ApplicationPageContent>
    </>
  );
}
